import {
  MarketingPageElementLocation,
  MarketingPageElementType,
  i18nFunct,
  lucidColors,
  sendBeaconEvent
} from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery, withPrefix } from 'gatsby';
import kebabCase from 'lodash.kebabcase';
import React, { useContext } from 'react';
import LazyLoad from 'react-lazyload';

import Link from 'components/link/link';
import LucidContainer from 'components/lucid-container/lucid-container';
import LayoutContext from 'contexts/layoutContext';
import { Drupal_Node, FooterSocialIconsNewJsonEdge } from 'generated/graphql';
import { LucidTheme, MenuLink } from 'types/types';
import StyledSelect from './styled-select';
import { languages } from '../../../data/languages';

interface Switcher {
  label: string;
  value: string;
}

interface Props {
  padFooter?: boolean;
  entityTranslations?: Drupal_Node[];
  menu: MenuLink[];
  showSecurity?: boolean;
  isVisio?: boolean;
}

const appLinks = [
  {
    url:
      'https://itunes.apple.com/us/app/lucidchart-flowchart-diagram-visio-viewer/id611543423?mt=8',
    image:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/footer/app-store-white.svg',
    alt: 'Apple App Store',
    detail: 'app-store'
  },
  {
    url:
      'https://play.google.com/store/apps/details?id=com.lucidchart.android.chart&amp;hl=en',
    image:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/footer/google-play.svg',
    alt: 'Google play Store',
    detail: 'google-play'
  }
];

const FooterNew = ({ entityTranslations, menu, isVisio }: Props) => {
  const { language } = useContext(LayoutContext);
  const { shortLangPrefix, i18nKey } = language;

  const { allFooterSocialIconsNewJson, i18nData } = useStaticQuery(
    graphql`
      {
        allFooterSocialIconsNewJson {
          edges {
            node {
              name
              logo
              url
            }
          }
        }
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_footer_about_privacy
                mktg_footer_legal
              }
            }
          }
        }
      }
    `
  );

  const i18n = i18nFunct(i18nData, i18nKey);

  const socialIcons = allFooterSocialIconsNewJson.edges as FooterSocialIconsNewJsonEdge[];
  const defaultLinks = [
    {
      url: withPrefix('/privacy'),
      text: i18n('mktg-footer-about-privacy')
    },
    {
      url: withPrefix('/legal'),
      text: i18n('mktg-footer-legal')
    }
  ];

  const visioQueryParams = '?type=visio';

  const disabledLanguages = ['da', 'sv'];
  const switcherValues = Object.keys(languages)
    .filter(thisLang => !disabledLanguages.includes(thisLang))
    .reduce<Record<string, Switcher>>((obj, thisLangPrefix) => {
      const { shortLangPrefix: thisShortLangPrefix } = languages[
        thisLangPrefix
      ];
      const { entityUrl } =
        (entityTranslations || []).find(
          ({ status, entityLanguage }) =>
            status && entityLanguage!.id === thisLangPrefix
        ) || {};

      obj[thisShortLangPrefix] = {
        value: entityUrl?.path
          ? entityUrl.path
          : `/${thisShortLangPrefix === 'en' ? '' : thisShortLangPrefix}`,
        label: languages[thisLangPrefix].name
      };

      return obj;
    }, {});

  interface CustomOptionsProps {
    data: {
      value: string;
      label: string;
    };
    isSelected: boolean;
  }

  const customOption = ({ data, isSelected }: CustomOptionsProps) => (
    <Link
      className={`language-switcher__link ${
        isSelected ? 'language-switcher__link--selected' : ''
        }`}
      to={data.value}
    >
      {data.label}
    </Link>
  );

  const showLangSelect = !disabledLanguages.includes(shortLangPrefix);

  return (
    <footer
      className="lucidchart-footer-new"
      css={theme => ({
        display: 'none',
        paddingTop: theme.space[3],
        background: theme.colors.coolGrey[0]
      })}
    >
      <LucidContainer flex className="footer-top">
        <div
          className="footer-top__links"
          css={(theme: LucidTheme) => ({
            display: 'flex',
            flex: '0 1 100%',
            justifyContent: 'space-between',
            flexWrap: 'wrap',

            [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
              flex: '0 1 100%'
            }
          })}
        >
          {menu.map(item => {
            return (
              <ul
                key={item.title}
                className="footer-top__link-group"
                css={(theme: LucidTheme) => ({
                  flex: '0 1 20%',
                  listStyle: 'none',
                  margin: '0 0 2rem 0',

                  [`@media screen and (max-width: ${theme.breakpoints.tablet})`]: {
                    flex: '0 1 31%',
                    marginBottom: '2.5rem'
                  },
                  [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
                    flex: '0 1 49%'
                  }
                })}
              >
                <li
                  className="footer-top__link-group-heading"
                  css={{
                    fontSize: '1.625rem',
                    lineHeight: '1.4',
                    fontWeight: 600,
                    marginBottom: '1rem'
                  }}
                >
                  {item.title}
                </li>
                {item.links.map(subitem => {
                  const href =
                    isVisio && subitem.fieldKey === 'pricing'
                      ? `${subitem.link.url!.path}${visioQueryParams}`
                      : subitem.link.url!.path;

                  const beaconEvent = subitem.fieldBeaconElementDetail
                    ? {
                      name: 'clickedMarketingPageElement',
                      elementLocation: MarketingPageElementLocation.footer,
                      elementDetail: subitem.fieldBeaconElementDetail,
                      elementType: MarketingPageElementType.link
                    }
                    : null;

                  return (
                    <li
                      key={subitem.title}
                      className="footer-top__link-group-item"
                      css={{
                        margin: '0.5rem 0',
                        paddingRight: '1.5rem'
                      }}
                    >
                      <Link
                        beaconEvent={beaconEvent}
                        className="footer-top__link-group-link"
                        data-km-trigger={`footer-${kebabCase(
                          subitem.fieldKmEventName
                        )}`}
                        onClick={() => subitem.fieldBeaconElementDetail}
                        to={href}
                        underline="hover"
                      >
                        {subitem.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            );
          })}
        </div>
      </LucidContainer>

      <div
        className="footer-bottom"
        css={{
          padding: '1.5rem 0',
          background: lucidColors.coolGrey[5]
        }}
      >
        <LucidContainer flex css={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <div
            className="footer-bottom__left"
            css={(theme: LucidTheme) => ({
              flex: '0 1 40%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'baseline',
              [`@media screen and (max-width: ${theme.breakpoints[2]})`]: {
                flex: '1 1 100%'
              }
            })}
          >
            {showLangSelect && (
              <StyledSelect
                menuIsOpen
                classNamePrefix="language-switcher"
                components={{ Option: customOption }}
                isSearchable={false}
                onClick={() =>
                  sendBeaconEvent({
                    name: 'clickedMarketingPageElement',
                    elementLocation: MarketingPageElementLocation.footer,
                    elementDetail: 'lang-toggle',
                    elementType: MarketingPageElementType.link
                  })}
                options={Object.values(switcherValues)}
                value={switcherValues[shortLangPrefix]}
              />
            )}
            {defaultLinks.map(link => (
              <Link
                key={link.text}
                css={{
                  marginRight: '1rem',
                  fontWeight: 400,
                  '&:last-child': { marginRight: '0' }
                }}
                to={link.url}
              >
                {link.text}
              </Link>
            ))}
          </div>
          <div
            className="footer-bottom__right"
            css={(theme: LucidTheme) => ({
              flex: '0 1 60%',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              [`@media screen and (max-width: ${theme.breakpoints[2]})`]: {
                flex: '1 1 100%',
                marginTop: '2rem',
                flexWrap: 'wrap'
              }
            })}
          >
            <ul
              className="footer-bottom__social-links"
              css={(theme: LucidTheme) => ({
                display: 'flex',
                flex: '0 1 215px',
                margin: `0 ${appLinks.length > 0 ? '2.5rem' : '0'} 0 0`,
                listStyle: 'none',
                color: theme.colors.primary,
                justifyContent: 'space-between',
                [`@media screen and (max-width: ${theme.breakpoints[2]})`]: {
                  flex: '1 1 290px'
                }
              })}
            >
              {socialIcons.map(socialIcon => {
                const icon = socialIcon.node;
                return (
                  <li key={icon.name}>
                    <a
                      href={icon.url!}
                      onClick={() =>
                        sendBeaconEvent({
                          name: 'clickedMarketingPageElement',
                          elementLocation: MarketingPageElementLocation.footer,
                          elementDetail: icon.name,
                          elementType: MarketingPageElementType.link
                        })}
                    >
                      <LazyLoad offset={300}>
                        <img
                          alt={icon.name!}
                          css={{ height: '20px' }}
                          src={icon.logo!}
                        />
                      </LazyLoad>
                    </a>
                  </li>
                );
              })}
            </ul>
            {appLinks.length > 0 && (
              <div className="footer-bottom__app-links">
                {appLinks.map(link => (
                  <a
                    key={link.detail}
                    css={(theme: LucidTheme) => ({
                      display: 'inline-block',
                      marginRight: '1rem',
                      '&:last-child': {
                        marginRight: '0'
                      },
                      [`@media screen and (max-width: ${theme.breakpoints[4]})`]: {
                        display: 'block',
                        marginRight: '0',
                        textAlign: 'right'
                      }
                    })}
                    href={link.url}
                    onClick={() =>
                      sendBeaconEvent({
                        name: 'clickedMarketingPageElement',
                        elementLocation: MarketingPageElementLocation.footer,
                        elementDetail: link.detail,
                        elementType: MarketingPageElementType.link
                      })}
                  >
                    <LazyLoad offset={300}>
                      <img
                        alt={link.alt}
                        css={{ height: '40px' }}
                        src={link.image}
                      />
                    </LazyLoad>
                  </a>
                ))}
              </div>
            )}
          </div>
          <div
            css={{
              marginTop: '.25rem',
              fontSize: '0.75rem',
              flex: '1 1 100%',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <span aria-label="Copyright" role="img">
              &copy;
            </span>
            {` ${new Date().getFullYear()} Lucid Software Inc.`}
          </div>
        </LucidContainer>
      </div>
    </footer>
  );
};

export default FooterNew;
